/**
 * <td-product-acc> — animates the PDP info accordions (snippets/td-product-accordions.liquid).
 *
 * The items stay native <details>/<summary>, so they work without JS and keep their keyboard and
 * screen reader behaviour. This element only intercepts the summary toggle to animate the panel
 * height open and closed. With prefers-reduced-motion it does nothing and the browser toggles
 * instantly.
 */
if (!customElements.get('td-product-acc')) {
  const DURATION = 250;
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  class TdProductAcc extends HTMLElement {
    constructor() {
      super();
      this.onSummaryClick = this.onSummaryClick.bind(this);
      /** @type {WeakMap<HTMLDetailsElement, Animation>} */
      this.animations = new WeakMap();
    }

    /**
     * Binds the summary listener (re-run on theme editor re-render).
     * @returns {void}
     */
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', this.onSummaryClick);
    }

    /**
     * Removes the listener.
     * @returns {void}
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.onSummaryClick);
    }

    /**
     * Takes over the toggle for this element's own accordion summaries.
     * @param {MouseEvent} event
     * @returns {void}
     */
    onSummaryClick(event) {
      if (this.reducedMotion?.matches) return;
      const summary = event.target instanceof Element ? event.target.closest('summary') : null;
      const details = summary?.parentElement;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('td-product-acc__item')) return;
      if (details.closest('td-product-acc') !== this) return;
      const panel = details.querySelector(':scope > .td-product-acc__panel');
      if (!panel || typeof panel.animate !== 'function') return;

      event.preventDefault();
      if (details.open && !details.dataset.closing) {
        this.close(details, panel);
      } else {
        this.open(details, panel);
      }
    }

    /**
     * Opens the item and grows the panel from its current height to its natural height.
     * @param {HTMLDetailsElement} details
     * @param {HTMLElement} panel
     * @returns {void}
     */
    open(details, panel) {
      const from = details.open ? panel.offsetHeight : 0;
      this.animations.get(details)?.cancel();
      delete details.dataset.closing;
      details.open = true;
      this.run(details, panel, from, panel.scrollHeight);
    }

    /**
     * Shrinks the panel to zero, then closes the item.
     * @param {HTMLDetailsElement} details
     * @param {HTMLElement} panel
     * @returns {void}
     */
    close(details, panel) {
      const from = panel.offsetHeight;
      this.animations.get(details)?.cancel();
      details.dataset.closing = 'true';
      const animation = this.run(details, panel, from, 0);
      animation.addEventListener('finish', () => {
        details.open = false;
        delete details.dataset.closing;
      });
    }

    /**
     * Animates the panel's height and opacity, clipping overflow while it runs.
     * @param {HTMLDetailsElement} details
     * @param {HTMLElement} panel
     * @param {number} from Start height in px.
     * @param {number} to End height in px.
     * @returns {Animation}
     */
    run(details, panel, from, to) {
      panel.style.overflow = 'hidden';
      const animation = panel.animate(
        [
          { height: `${from}px`, opacity: from ? 1 : 0 },
          { height: `${to}px`, opacity: to ? 1 : 0 },
        ],
        { duration: DURATION, easing: EASING }
      );
      this.animations.set(details, animation);
      const cleanup = () => {
        panel.style.overflow = '';
        if (this.animations.get(details) === animation) this.animations.delete(details);
      };
      animation.addEventListener('finish', cleanup);
      animation.addEventListener('cancel', cleanup);
      return animation;
    }
  }

  customElements.define('td-product-acc', TdProductAcc);
}
